import { Component } from "@theme/component";

/**
 * Navigation component - Mobile and Desktop Navigation Handler
 *
 * Manages comprehensive navigation interactions including:
 * - Mobile hamburger menu toggle
 * - Sub-navigation drawers and overlays
 * - Accordion menus
 * - Desktop drawer navigation
 * - Promo bar carousel (Swiper)
 * - Feature callout carousels (Swiper)
 * - Gorgias chat integration
 * - Back navigation
 * - Overlay click-to-close
 *
 * @extends {Component}
 */
/* global GorgiasChat */
class NavigationComponent extends Component {
  constructor() {
    super();
    this.dom = {};
    this.swiperInstances = [];
    this.eventHandlers = [];
    this.scrollbarWidth = 0;
    /** @type {HTMLElement|null} Stores the trigger that opened the current drawer for focus return */
    this._lastActiveTrigger = null;
    /** @type {((e: KeyboardEvent) => void)|null} */
    this._focusTrapHandler = null;
    /** @type {HTMLElement|null} */
    this._focusTrapContainer = null;
  }

  /**
   * Calculate the width of the browser scrollbar
   * @returns {number} The scrollbar width in pixels
   * @private
   */
  #getScrollbarWidth() {
    // Cache the scrollbar width calculation
    if (this.scrollbarWidth === 0) {
      this.scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    }
    return this.scrollbarWidth;
  }

  /**
   * Lock body scroll and compensate for scrollbar width to prevent layout shift
   * @private
   */
  #lockBodyScroll() {
    const scrollbarWidth = this.#getScrollbarWidth();
    document.body.style.overflow = "hidden";
    document.body.style.paddingRight = `${scrollbarWidth}px`;

    // Apply padding to fixed/sticky elements to prevent layout shift
    const stickyHeader = document.querySelector("sticky-header-wrapper");
    if (stickyHeader instanceof HTMLElement) {
      stickyHeader.style.paddingRight = `${scrollbarWidth}px`;
    }
  }

  /**
   * Unlock body scroll and remove scrollbar compensation
   * @private
   */
  #unlockBodyScroll() {
    document.body.style.overflow = "";
    document.body.style.paddingRight = "";

    // Remove padding from fixed/sticky elements
    const stickyHeader = document.querySelector("sticky-header-wrapper");
    if (stickyHeader instanceof HTMLElement) {
      stickyHeader.style.paddingRight = "";
    }
  }

  /**
   * Called when component is added to the DOM
   */
  connectedCallback() {
    super.connectedCallback();
    this.#cacheDom();
    this.#bindUIActions();
  }

  /**
   * Called when component is removed from the DOM
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cleanup();
  }

  /**
   * Cache all DOM elements for navigation
   * @private
   */
  #cacheDom() {
    this.dom.navToggle = this.querySelector(".js-nav__toggle");
    this.dom.navDrawer = this.querySelector(".js-header__navigation");
    this.dom.navTriggers = this.querySelectorAll(".js-subNavTrigger");
    this.dom.navBack = this.querySelectorAll(".js-collapsible-back");
    this.dom.navAccordions = this.querySelectorAll(".js-menuAccordion");
    this.dom.desktopNavTriggers = this.querySelectorAll(".sidebarMenu__mainTrigger");
    this.dom.overlay = this.querySelector(".sidebarMenu__drawerOverlay");
    this.dom.drawerTriggers = this.querySelectorAll(".sidebarMenu__drawerTrigger");
    this.dom.drawerWrappers = this.querySelectorAll(".sidebarMenu__drawerWrapper");
    this.dom.closeNavBtns = this.querySelectorAll(".header__closeNav");
    // Read before #syncNavDialogState() can strip it at desktop widths.
    this.dom.navDialogLabel = this.dom.navDrawer?.getAttribute("aria-label") || "Navigation menu";
    this.dom.closeNavAnchor = this.querySelector(".header__storeLocatorClose");
    this.dom.mobileNavClose = this.querySelector(".js-mobile-nav-close");
    this.dom.triggerGorgiasChat = this.querySelector(".js-sidebarMenu__contactTopItem--chat");
    this.dom.promoBarSlider = document.querySelector(".js-promoBar__carousel");
    this.dom.promoSlides = document.querySelectorAll(".announcement__bar");
    this.dom.header = this.querySelector("header-component");
  }

  /**
   * Bind all UI event handlers
   * @private
   */
  #bindUIActions() {
    // Apply the dialog/inert semantics that match the current viewport
    this.#syncNavDialogState();

    // Gorgias chat trigger
    if (this.dom.triggerGorgiasChat) {
      const chatHandler = () => this.#handleGorgiasChat();
      this.dom.triggerGorgiasChat.addEventListener("click", chatHandler);
      this.eventHandlers.push({ element: this.dom.triggerGorgiasChat, event: "click", handler: chatHandler });
    }

    // Mobile hamburger toggle
    if (this.dom.navToggle) {
      const toggleHandler = () => this.#toggleMobileNav();
      this.dom.navToggle.addEventListener("click", toggleHandler);
      this.eventHandlers.push({ element: this.dom.navToggle, event: "click", handler: toggleHandler });
    }

    // SR-only close button inside mobile nav dialog (#226)
    if (this.dom.mobileNavClose) {
      const mobileCloseHandler = () => this.#closeMobileNav();
      this.dom.mobileNavClose.addEventListener("click", mobileCloseHandler);
      this.eventHandlers.push({ element: this.dom.mobileNavClose, event: "click", handler: mobileCloseHandler });
    }

    // Sub-navigation triggers (click)
    this.dom.navTriggers.forEach(trigger => {
      const clickHandler = () => this.#handleNavTriggerClick(trigger);
      trigger.addEventListener("click", clickHandler);
      this.eventHandlers.push({ element: trigger, event: "click", handler: clickHandler });

      // Desktop hover behavior
      if (window.innerWidth >= 1280) {
        const hoverHandler = () => this.#handleNavTriggerHover(trigger);
        trigger.addEventListener("mouseover", hoverHandler);
        this.eventHandlers.push({ element: trigger, event: "mouseover", handler: hoverHandler });
      }
    });

    // Handle viewport resize to manage inert state
    let resizeTimer;
    let lastViewport = `${window.innerWidth}x${window.innerHeight}`;
    const resizeHandler = () => {
      // Only freeze animations when the viewport DIMENSIONS actually changed.
      // Synthetic window "resize" events (dispatched by third-party scripts,
      // e.g. an old gecx-layout-fix.js) otherwise flip resize-animation-stopper
      // (animation: none !important) and hard-reset CSS animations like the
      // marquee — and the body class add/remove can even feed observer loops.
      const viewport = `${window.innerWidth}x${window.innerHeight}`;
      if (viewport !== lastViewport) {
        lastViewport = viewport;
        document.body.classList.add("resize-animation-stopper");
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          document.body.classList.remove("resize-animation-stopper");
        }, 400);
      }

      if (document.querySelector(".sidebarMenu__drawerWrapper.is--active")) {
        this.#positionCloseButtons();
      }

      this.#syncNavDialogState();
    };
    window.addEventListener("resize", resizeHandler);
    this.eventHandlers.push({ element: window, event: "resize", handler: resizeHandler });

    // Back navigation buttons
    this.dom.navBack.forEach(back => {
      const backHandler = () => this.#handleNavBack(back);
      back.addEventListener("click", backHandler);
      this.eventHandlers.push({ element: back, event: "click", handler: backHandler });
    });

    // Accordion menus
    this.dom.navAccordions.forEach(accordion => {
      const accordionHandler = () => this.#handleAccordion(accordion);
      accordion.addEventListener("click", accordionHandler);
      this.eventHandlers.push({ element: accordion, event: "click", handler: accordionHandler });
    });

    // Desktop drawer triggers
    // Desktop drawer triggers
    this.dom.desktopNavTriggers.forEach(trigger => {
      const desktopHandler = e => {
        if (window.innerWidth >= 1280) {
          this.#handleDesktopDrawer(trigger, e);
        }
      };
      trigger.addEventListener("click", desktopHandler);
      this.eventHandlers.push({ element: trigger, event: "click", handler: desktopHandler });
    });

    // Drawer triggers
    this.dom.drawerTriggers.forEach(drawerTrigger => {
      const drawerHandler = e => this.#handleDrawerTrigger(drawerTrigger, e);
      drawerTrigger.addEventListener("click", drawerHandler);
      this.eventHandlers.push({ element: drawerTrigger, event: "click", handler: drawerHandler });
    });

    // Overlay click to close
    if (this.dom.overlay) {
      const overlayHandler = () => this.#closeDrawers();
      this.dom.overlay.addEventListener("click", overlayHandler);
      this.eventHandlers.push({ element: this.dom.overlay, event: "click", handler: overlayHandler });
    }

    // Prevent clicks inside navigation drawer from closing it
    if (this.dom.navDrawer) {
      const stopPropagationHandler = e => e.stopPropagation();
      this.dom.navDrawer.addEventListener("click", stopPropagationHandler);
      this.eventHandlers.push({ element: this.dom.navDrawer, event: "click", handler: stopPropagationHandler });
    }

    // Close button — one per drawer dialog (see blocks/_navigation.liquid)
    this.dom.closeNavBtns.forEach(closeNavBtn => {
      const closeHandler = () => this.#closeDrawers();
      closeNavBtn.addEventListener("click", closeHandler);
      this.eventHandlers.push({ element: closeNavBtn, event: "click", handler: closeHandler });
    });

    // Header click to close navigation drawer (but not when clicking nav toggle or inside drawer)
    // if (this.dom.header) {
    //   const headerClickHandler = e => {
    //     // Don't close if clicking on nav toggle or inside navigation drawer
    //     if (this.dom.navToggle?.contains(e.target) || this.dom.navDrawer?.contains(e.target)) {
    //       return;
    //     }
    //     // Close drawer if it's open
    //     if (this.dom.navDrawer?.getAttribute("aria-hidden") === "false") {
    //       this.#closeDrawers();
    //     }
    //   };
    //   this.dom.header.addEventListener("click", headerClickHandler);
    //   this.eventHandlers.push({ element: this.dom.header, event: "click", handler: headerClickHandler });
    // }
  }

  /**
   * Handle Gorgias chat trigger
   * @private
   */
  #handleGorgiasChat() {
    if (window.innerWidth < 1024) {
      this.#closeMobileNav();
    } else {
      this.#closeDrawers();
    }

    // Open Gorgias chat via the theme helper (snippets/gorgias-chat.liquid). The helper
    // un-hides the widget container before calling the supported GorgiasChat.open().
    if (window.bwwGorgiasChat) {
      window.bwwGorgiasChat.open();
    } else if (typeof GorgiasChat !== "undefined") {
      GorgiasChat.open();
    }
  }

  /**
   * Initialize carousels in the drawer when it becomes visible
   * Waits for carousel.js to be ready before initializing
   * @private
   */
  #initializeDrawerCarousels() {
    const initialize = () => {
      if (typeof window.initCarousels === "function") {
        // false = don't skip hidden, since drawer is now visible
        window.initCarousels(".swiper__carousel", false);
      }
    };

    // Check if carousel.js is already ready
    if (typeof window.initCarousels === "function") {
      // Carousel.js is loaded, initialize immediately
      initialize();
    } else {
      // Wait for carousel:ready event
      const handleCarouselReady = () => {
        initialize();
        document.removeEventListener("carousel:ready", handleCarouselReady);
      };
      document.addEventListener("carousel:ready", handleCarouselReady);
    }
  }

  /**
   * Get all visible, focusable elements within a container.
   * Accounts for desktop drawer structure where drawer triggers span multiple wrappers.
   * @param {HTMLElement} container
   * @returns {HTMLElement[]}
   * @private
   */
  #getFocusableElements(container) {
    const selector = [
      "a[href]",
      "button:not([disabled])",
      "input:not([disabled])",
      "select:not([disabled])",
      "textarea:not([disabled])",
      '[tabindex]:not([tabindex="-1"])',
    ].join(", ");

    return [...container.querySelectorAll(selector)].filter(el => {
      if (el.closest("[inert]")) return false;
      if (el.offsetParent === null && getComputedStyle(el).position !== "fixed") return false;

      if (el.classList.contains("sidebarMenu__drawerTrigger")) {
        return el.classList.contains("is--visible");
      }

      const wrapper = el.closest(".sidebarMenu__drawerWrapper");
      if (wrapper && !wrapper.classList.contains("is--active")) return false;

      return true;
    });
  }

  /**
   * Trap keyboard focus within a container so Tab / Shift+Tab wraps around
   * @param {HTMLElement} container
   * @private
   */
  #activateFocusTrap(container) {
    this.#deactivateFocusTrap();

    this._focusTrapContainer = container;
    this._focusTrapHandler = e => {
      // A dialog must be dismissible from the keyboard — without this the trap
      // below leaves the close button as the only way out.
      if (e.key === "Escape") {
        e.preventDefault();
        this.#closeDrawers();
        return;
      }

      if (e.key !== "Tab") return;

      const focusable = this.#getFocusableElements(this._focusTrapContainer);
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", this._focusTrapHandler);
  }

  /**
   * Remove the active focus trap
   * @private
   */
  #deactivateFocusTrap() {
    if (this._focusTrapHandler) {
      document.removeEventListener("keydown", this._focusTrapHandler);
      this._focusTrapHandler = null;
      this._focusTrapContainer = null;
    }
  }

  /**
   * Sync the hamburger toggle's icons and accessible state with the drawer
   * @param {boolean} isOpen - Whether the mobile nav drawer is open
   * @private
   */
  #setNavToggleState(isOpen) {
    if (!this.dom.navToggle) return;

    const hamburger = this.dom.navToggle.querySelector(".icon--menu");
    const close = this.dom.navToggle.querySelector(".icon--menuClose");

    if (hamburger) hamburger.style.display = isOpen ? "none" : "block";
    if (close) close.style.display = isOpen ? "block" : "none";

    this.dom.navToggle.setAttribute("aria-expanded", isOpen ? "true" : "false");
    this.dom.navToggle.setAttribute("aria-label", isOpen ? "Close menu" : "Open menu");
  }

  /**
   * Toggle mobile navigation drawer (hamburger button handler)
   * @private
   */
  #toggleMobileNav() {
    if (!this.dom.navToggle || !this.dom.navDrawer) return;

    const isOpen = this.dom.navToggle.getAttribute("aria-expanded") === "true";

    if (!isOpen) {
      this.#setNavToggleState(true);

      if (this.dom.navDrawer.getAttribute("aria-hidden") === "true") {
        this.dom.navDrawer.setAttribute("aria-hidden", "false");
        this.dom.navDrawer.removeAttribute("inert");
        document.body.classList.add("drawer-open", "menu--open");

        if (this.dom.overlay) {
          this.dom.overlay.classList.add("is--active");
        }

        this.#initializeDrawerCarousels();

        const firstFocusable = this.dom.navDrawer.querySelector(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );

        if (firstFocusable) {
          firstFocusable.focus();
        }

        this.#activateFocusTrap(this.dom.navDrawer);
      }
    } else {
      this.#closeMobileNav();
    }
  }

  /**
   * Close the mobile navigation drawer and return focus to the toggle
   * @private
   */
  #closeMobileNav() {
    if (!this.dom.navDrawer) return;

    this.#setNavToggleState(false);

    if (this.dom.navDrawer.getAttribute("aria-hidden") === "false") {
      const focusedElement = this.dom.navDrawer.querySelector(":focus");

      if (focusedElement && focusedElement instanceof HTMLElement) {
        focusedElement.blur();
      }

      this.dom.navDrawer.setAttribute("aria-hidden", "true");
      this.dom.navDrawer.setAttribute("inert", "");
      this.#deactivateFocusTrap();
      document.body.classList.remove("drawer-open", "menu--open");

      if (this.dom.overlay) {
        this.dom.overlay.classList.remove("is--active");
      }
    }

    this.dom.navToggle?.focus();
  }

  /**
   * Handle navigation trigger click
   * @param {HTMLElement} trigger - The trigger element
   * @private
   */
  #handleNavTriggerClick(trigger) {
    if (trigger.dataset.navChild === "false") return;
    const subMenu = trigger.nextElementSibling;
    if (subMenu) {
      subMenu.classList.add("is--active");
      if (this.dom.navDrawer) {
        this.dom.navDrawer.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }
    }
  }

  /**
   * Handle navigation trigger hover (desktop only)
   * @param {HTMLElement} trigger - The trigger element
   * @private
   */
  #handleNavTriggerHover(trigger) {
    // Set all other triggers as inactive
    this.dom.navTriggers.forEach(t => {
      if (trigger.dataset.navChild === "true") {
        t.classList.add("is--inactive");
      } else {
        t.classList.remove("is--inactive");
      }
    });
    trigger.classList.remove("is--inactive");

    // Close other open submenus
    const openSubMenus = this.querySelectorAll(".sidebarMenu__subMenu.is--active");
    openSubMenus.forEach(openSubMenu => {
      if (openSubMenu !== trigger.nextElementSibling) {
        openSubMenu.classList.remove("is--active");
      }
    });

    if (trigger.dataset.navChild === "false") return;
    const subMenu = trigger.nextElementSibling;
    if (subMenu) {
      subMenu.classList.add("is--active");
    }
  }

  /**
   * Handle back navigation
   * @param {HTMLElement} back - The back button element
   * @private
   */
  #handleNavBack(back) {
    const subMenu = back.closest(".is--active");
    if (subMenu) {
      subMenu.classList.remove("is--active");
    }
  }

  /**
   * Handle accordion menu toggle
   * @param {HTMLElement} accordion - The accordion element
   * @private
   */
  #handleAccordion(accordion) {
    const content = accordion.nextElementSibling;
    if (!content) return;

    if (accordion.classList.contains("isExpanded")) {
      // Collapse
      accordion.classList.remove("isExpanded");
      content.style.height = "0px";
      content.classList.remove("isOpen");
    } else {
      // Collapse any other expanded accordion
      const expanded = this.querySelector(".js-menuAccordion.isExpanded");
      if (expanded && expanded !== accordion) {
        expanded.classList.remove("isExpanded");
        const expandedContent = expanded.nextElementSibling;
        if (expandedContent) {
          expandedContent.style.height = "0px";
          expandedContent.classList.remove("isOpen");
        }
      }

      // Expand current accordion
      accordion.classList.add("isExpanded");
      content.classList.add("isOpen");
      content.style.height = content.scrollHeight + "px";
    }
  }

  /**
   * Apply the dialog semantics that match the current viewport.
   *
   * Below 1280px .header__navigation is a modal drawer, so it keeps
   * role="dialog" / aria-modal / aria-hidden / inert. At 1280px and up it is
   * the static header bar: it is not a dialog, and it must not stay
   * aria-hidden — its links are visible and focusable there, which is both an
   * aria-hidden-focus violation and the reason the whole desktop nav was
   * invisible to assistive tech.
   * @private
   */
  #syncNavDialogState() {
    const nav = this.dom.navDrawer;

    if (!nav) return;

    if (window.innerWidth >= 1280) {
      nav.removeAttribute("role");
      nav.removeAttribute("aria-modal");
      nav.removeAttribute("aria-label");
      nav.removeAttribute("aria-hidden");
      nav.removeAttribute("inert");
      return;
    }

    nav.setAttribute("role", "dialog");
    nav.setAttribute("aria-modal", "true");
    nav.setAttribute("aria-label", this.dom.navDialogLabel);

    // Coming back from a desktop width: re-establish the closed drawer state,
    // since aria-hidden (which also drives the slide-in transform) was removed.
    if (!nav.hasAttribute("aria-hidden")) {
      nav.setAttribute("aria-hidden", "true");
      this.#setNavToggleState(false);
    }

    if (nav.getAttribute("aria-hidden") === "true") {
      nav.setAttribute("inert", "");
    } else {
      nav.removeAttribute("inert");
    }
  }

  /**
   * Expose only the open drawer panel as a dialog.
   *
   * A closed wrapper carrying role="dialog" shows up in the accessibility tree
   * as an empty dialog, so the role and its name are attached on activation and
   * dropped again on close.
   * @private
   */
  #syncDrawerDialogState() {
    this.dom.drawerWrappers.forEach(wrapper => {
      if (wrapper.classList.contains("is--active")) {
        wrapper.setAttribute("role", "dialog");
        wrapper.setAttribute("aria-label", wrapper.dataset.dialogLabel || "");
      } else {
        wrapper.removeAttribute("role");
        wrapper.removeAttribute("aria-label");
      }
    });
  }

  /**
   * Size and place the per-dialog close buttons.
   *
   * The close button used to live in .header__storeLocatorClose and was
   * positioned against it. It now lives inside each role="dialog" drawer
   * wrapper, so each dialog owns its own close control. That makes
   * .header__navigation its containing block — a different box. Measuring the
   * old anchor and publishing the offsets as custom properties keeps the
   * rendered button in exactly the same place, at any nav width.
   * @private
   */
  #positionCloseButtons() {
    const anchor = this.dom.closeNavAnchor;
    const nav = this.dom.navDrawer;

    if (!anchor || !nav) return;

    const anchorRect = anchor.getBoundingClientRect();
    const navRect = nav.getBoundingClientRect();

    // Mirrors the old button's offsets against the anchor:
    // lg:-left-[33px] -ml-[0.5px], top-0 -mt-[20px], w-[calc(100%+16px)], h-full.
    nav.style.setProperty("--nav-close-left", `${anchorRect.left - navRect.left - 33.5}px`);
    nav.style.setProperty("--nav-close-top", `${anchorRect.top - navRect.top - 20}px`);
    nav.style.setProperty("--nav-close-width", `${anchorRect.width + 16}px`);
    nav.style.setProperty("--nav-close-height", `${anchorRect.height}px`);
  }

  /**
   * Focus the close button belonging to the drawer dialog that is open
   * @private
   */
  #focusActiveCloseButton() {
    const activeWrapper = document.querySelector(".sidebarMenu__drawerWrapper.is--active");
    activeWrapper?.querySelector(".header__closeNav")?.focus();
  }

  /**
   * Handle desktop drawer trigger
   * @param {HTMLElement} trigger - The trigger element
   * @param {Event} e - The click event
   * @private
   */
  #handleDesktopDrawer(trigger, e) {
    const contactMenu = document.querySelector(".sidebarMenu__contact");
    const openDrawers = document.querySelectorAll(".sidebarMenu__drawerWrapper.is--active");

    // Close other open drawers
    openDrawers.forEach(openDrawer => {
      if (openDrawer !== e.target.closest(".sidebarMenu__drawerWrapper")) {
        openDrawer.classList.remove("is--active");
      }
    });

    const drawer = e.target.nextElementSibling;
    if (drawer && !drawer.classList.contains("is--active")) {
      this.#lockBodyScroll();
      drawer.classList.add("is--active");
      document.body.classList.add("menu--open");

      this.dom.drawerTriggers.forEach(drawerTrigger => {
        drawerTrigger.classList.add("is--visible");
        if (drawerTrigger.dataset.trigger === trigger.dataset.trigger) {
          drawerTrigger.classList.add("is--active");
        } else {
          drawerTrigger.classList.remove("is--active");
        }
      });

      // Move contact menu to drawer
      if (contactMenu) {
        const desktopDrawer = drawer.querySelector(".sidebarMenu__subNavWrapper");
        if (desktopDrawer) {
          desktopDrawer.appendChild(contactMenu);
        }
      }

      if (this.dom.overlay) {
        this.dom.overlay.classList.add("is--active");
      }
    }

    // The active wrapper's close button is revealed by CSS; measure it first so
    // it is already in place when it takes focus.
    this.#syncDrawerDialogState();
    this.#positionCloseButtons();

    this._lastActiveTrigger = trigger;

    this.#focusActiveCloseButton();

    if (this.dom.navDrawer) {
      this.#activateFocusTrap(this.dom.navDrawer);
    }
  }

  /**
   * Handle drawer trigger click
   * @param {HTMLElement} drawerTrigger - The drawer trigger element
   * @param {Event} e - The click event
   * @private
   */
  #handleDrawerTrigger(drawerTrigger, e) {
    // Reset inactive states
    this.dom.navTriggers.forEach(t => {
      t.classList.remove("is--inactive");
    });

    const contactMenu = document.querySelector(".sidebarMenu__contact");
    const openDrawers = document.querySelectorAll(".sidebarMenu__drawerWrapper.is--active");

    // Close other open drawers
    openDrawers.forEach(openDrawer => {
      if (openDrawer !== e.target.closest(".sidebarMenu__drawerWrapper")) {
        openDrawer.classList.remove("is--active");
      }
    });

    const drawer = e.target.parentElement;
    if (drawer && !drawer.classList.contains("is--active")) {
      drawer.classList.add("is--active");
      document.body.classList.add("menu--open");

      // Move contact menu to drawer
      if (contactMenu) {
        const desktopDrawer = drawer.querySelector(".sidebarMenu__subNavWrapper");
        if (desktopDrawer) {
          desktopDrawer.appendChild(contactMenu);
        }
      }

      this.dom.drawerTriggers.forEach(trigger => trigger.classList.remove("is--active"));
      drawerTrigger.classList.add("is--active");

      if (this.dom.overlay) {
        this.dom.overlay.classList.add("is--active");
      }
      this.#lockBodyScroll();
    }

    this.#syncDrawerDialogState();
  }

  /**
   * Close all drawers and reset states
   * @private
   */
  #closeDrawers() {
    const drawers = document.querySelectorAll(".sidebarMenu__drawerWrapper.is--active");
    drawers.forEach(drawer => {
      drawer.classList.remove("is--active");
    });

    this.#syncDrawerDialogState();

    this.dom.drawerTriggers.forEach(trigger => {
      trigger.classList.remove("is--active");
      trigger.classList.remove("is--visible");
    });

    const openSubMenus = this.querySelectorAll(".sidebarMenu__subMenu.is--active");
    openSubMenus.forEach(openSubMenu => {
      openSubMenu.classList.remove("is--active");
    });

    this.dom.navTriggers.forEach(trigger => trigger.classList.remove("is--inactive"));

    // Close mobile navigation drawer if open
    if (this.dom.navDrawer?.getAttribute("aria-hidden") === "false") {
      // Blur any focused elements inside the drawer before hiding to prevent accessibility violation
      const focusedElement = this.dom.navDrawer.querySelector(":focus");
      if (focusedElement && focusedElement instanceof HTMLElement) {
        focusedElement.blur();
      }
      this.#setNavToggleState(false);
      this.dom.navDrawer.setAttribute("aria-hidden", "true");
      this.dom.navDrawer.setAttribute("inert", "");
      document.body.classList.remove("drawer-open", "menu--open");
    }

    if (this.dom.overlay) {
      this.dom.overlay.classList.remove("is--active");
    }

    this.#unlockBodyScroll();
    this.#deactivateFocusTrap();

    if (this._lastActiveTrigger && this._lastActiveTrigger.isConnected) {
      this._lastActiveTrigger.focus();
      this._lastActiveTrigger = null;
    } else {
      this._lastActiveTrigger = null;
      this.dom.navToggle?.focus();
    }
  }

  /**
   * Clean up event listeners and Swiper instances
   * @private
   */
  #cleanup() {
    // Remove all event listeners
    this.eventHandlers.forEach(({ element, event, handler }) => {
      if (element) {
        element.removeEventListener(event, handler);
      }
    });
    this.eventHandlers = [];

    // NOTE: Swiper destruction is purposely removed here.
    // The carousel.js library handles lifecycle or the instance should persist
    // to avoid visual flashes if this component is re-connected.
    this.swiperInstances = [];

    // Close drawers and reset state
    this.#closeDrawers();
  }
}

if (!window.customElements.get("navigation-component")) {
  window.customElements.define("navigation-component", NavigationComponent);
}
